// Browser-direct calls to the three LLM APIs (all send CORS headers; verified 2026-09-15). No proxy, no server.
import { t } from '@/i18n';
import type { Provider } from '@/types';
import { LLMError } from './types';

const BASES: Record<Provider, string> = {
  openai: 'https://api.openai.com',
  claude: 'https://api.anthropic.com',
  gemini: 'https://generativelanguage.googleapis.com',
};

function authHeaders(provider: Provider, apiKey: string): Record<string, string> {
  switch (provider) {
    case 'openai':
      return { authorization: `Bearer ${apiKey}` };
    case 'claude':
      return { 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' };
    case 'gemini':
      return { 'x-goog-api-key': apiKey };
  }
}

// Genuine throttling (429 that is not a billing problem) clears in seconds: a report with web search, or eight
// experts answering at once, can trip a tokens- or requests-per-minute limit on a low usage tier. Retry a couple
// of times, waiting what the provider asks for (Retry-After) or a short backoff, before giving up.
const RETRY_DELAYS_MS = [3000, 8000];
const MAX_RETRY_AFTER_MS = 20000;

function retryDelay(res: Response, attempt: number): number {
  const header = Number(res.headers.get('retry-after'));
  if (Number.isFinite(header) && header > 0) return Math.min(header * 1000, MAX_RETRY_AFTER_MS);
  return RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1];
}

function sleep(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new LLMError('aborted', t('llm.aborted')));
    }, { once: true });
  });
}

export async function providerFetch(provider: Provider, path: string, init: RequestInit, apiKey: string): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(authHeaders(provider, apiKey))) headers.set(k, v);
  for (let attempt = 0; ; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${BASES[provider]}${path}`, { ...init, headers });
    } catch (e) {
      if ((e as Error)?.name === 'AbortError') throw new LLMError('aborted', t('llm.aborted'));
      throw new LLMError('network', t('llm.connect', { host: BASES[provider] }));
    }
    if (res.ok) return res;
    const error = await toLLMError(res);
    if (error.code !== 'rate_limited' || attempt >= RETRY_DELAYS_MS.length) throw error;
    await sleep(retryDelay(res, attempt), init.signal);
  }
}

async function toLLMError(res: Response): Promise<LLMError> {
  let body = '';
  try {
    body = await res.text();
  } catch {
    // body unreadable
  }
  return classifyLLMError(res.status, body);
}

/** Billing signals, as distinct from throttling: OpenAI sends insufficient_quota with a 429, Anthropic "credit balance is too low" with a 400. */
const QUOTA_RE = /insufficient_quota|no credits|credit balance|out of credits|add credits|plan and billing/i;

/** Maps a provider's HTTP error (status + raw body) onto our error codes. Pure; exported for tests. */
export function classifyLLMError(status: number, body: string): LLMError {
  const { message, code } = extractError(body);
  const detail = message ?? body.slice(0, 200);
  const suffix = detail ? `: ${detail}` : '';
  if (status === 401 || status === 403 || (status === 400 && /api[_ ]?key/i.test(detail))) return new LLMError('invalid_key', t('llm.invalidKey', { status, detail: suffix }), status);
  if (QUOTA_RE.test(`${code ?? ''} ${detail}`)) return new LLMError('quota', t('llm.quota', { status, detail: suffix }), status);
  if (status === 429) return new LLMError('rate_limited', t('llm.rateLimited', { detail: suffix }), status);
  if (status === 400 && /context|token|too long|maximum|exceed/i.test(detail)) return new LLMError('context_too_long', t('llm.contextTooLong', { detail: suffix }), status);
  return new LLMError('upstream', t('llm.upstream', { status, detail: suffix }), status);
}

function extractError(text: string): { message?: string; code?: string } {
  try {
    const json = JSON.parse(text) as { error?: { message?: string; type?: string; code?: string | number } | string; message?: string };
    if (typeof json.error === 'string') return { message: json.error };
    const code = json.error?.code != null ? String(json.error.code) : json.error?.type;
    return { message: json.error?.message ?? json.message, code };
  } catch {
    return {};
  }
}
